// swapRequestsApi — Phase 1F. Manager dashboard view + officer request flow.
import { apiFetch } from "../../lib/api-client.js";

export type SwapStatus = "pending" | "approved" | "rejected" | "cancelled" | "completed";

export interface SwapRequestRow {
  id: string;
  tenant_id: string;
  requester_officer_id: string;
  requester_roster_entry_id: string;
  target_officer_id: string;
  target_roster_entry_id: string | null;
  reason: string | null;
  status: SwapStatus;
  requested_at: string;
  decided_at: string | null;
  decided_by_user_id: string | null;
  decision_notes: string | null;
  // joined display
  requester_name: string | null;
  target_name: string | null;
  requester_work_date: string | null;
  target_work_date: string | null;
  requester_shift_name: string | null;
  requester_shift_start: string | null;
  requester_shift_end: string | null;
  target_shift_name: string | null;
  target_shift_start: string | null;
  target_shift_end: string | null;
  requester_site_name: string | null;
  target_site_name: string | null;
}

export interface SwapListResponse { items: SwapRequestRow[]; correlation_id: string | null }

export class SwapApiError extends Error {
  status: number; code: string; correlationId: string | null; upstreamMessage?: string;
  constructor(status: number, code: string, cid: string | null, msg?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = cid; this.upstreamMessage = msg;
  }
}

export function friendlySwapError(err: unknown): string {
  if (err instanceof SwapApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403 && err.code === "forbidden_not_owner") return "您只能換自己的班。";
    if (err.status === 403 && err.code === "forbidden_manager_only") return "需要主管權限才能審核換班。";
    if (err.status === 403) return "您目前的角色不能執行此動作。";
    if (err.status === 404) return "找不到此換班單。";
    if (err.status === 409 && err.code === "duplicate_pending_request") return "已有待審核的換班請求。";
    if (err.status === 409 && err.code === "not_pending") return "這筆換班已決議過了。";
    if (err.status === 400 && err.code === "requester_entry_cancelled") return "原本的排班已被取消，無法換班。";
    if (err.status === 400 && err.code === "target_entry_officer_mismatch") return "對方這天的班別不是該名員工。";
    if (err.status === 400) return `請求格式不正確（${err.code}）。`;
    return `換班服務暫時無法存取（HTTP ${err.status}・code=${err.code}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<SwapApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let msg: string | undefined;
  try { const b = (await r.json()) as { error?: string; correlation_id?: string; message?: string }; code = b.error ?? code; cid = b.correlation_id ?? cid; msg = b.message; } catch {}
  return new SwapApiError(r.status, code, cid, msg);
}

export async function listSwapRequests(params: { tenantId?: string | null; status?: SwapStatus | "" } = {}): Promise<SwapListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.status) qs.set("status", params.status);
  const r = await apiFetch(`/v1/schedules/swap-requests${qs.toString() ? `?${qs}` : ""}`);
  if (!r.ok) throw await asError(r);
  return (await r.json()) as SwapListResponse;
}

export interface CreateSwapBody {
  requester_roster_entry_id: string;
  target_officer_id: string;
  target_roster_entry_id?: string | null;
  reason?: string | null;
}
export async function createSwapRequest(body: CreateSwapBody, tenantId?: string | null): Promise<{ id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/swap-requests${qs}`, {
    method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export type SwapDecision = "approved" | "rejected" | "cancelled";
export interface DecideOptions {
  notes?: string | null;
  /** Required when source row's target_officer_id is null and decision='approved'. */
  assigneeOfficerId?: string | null;
}
export async function decideSwapRequest(id: string, decision: SwapDecision, opts: DecideOptions = {}, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/swap-requests/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH", headers: { "content-type": "application/json" },
    body: JSON.stringify({
      decision,
      decision_notes: opts.notes ?? null,
      assignee_officer_id: opts.assigneeOfficerId ?? null,
    }),
  });
  if (!r.ok) throw await asError(r);
}
